import {
  Bell,
  Key,
  KeyRound,
  Layers,
  Lock,
  Monitor,
  Server,
  Settings as SettingsIcon,
  ShieldOff,
  Table as TableIcon,
  User,
} from "lucide-react";
import * as React from "react";

import { NotificationPreferences } from "@/pages/me/NotificationPreferences";
import { PersonalOverrides } from "@/pages/me/PersonalOverrides";
import { TablePresets } from "@/pages/me/TablePresets";
import { AccountDeactivatePage } from "@/pages/settings/AccountDeactivatePage";
import { ApiTokensPage } from "@/pages/settings/ApiTokensPage";
import { ChangePassword } from "@/pages/settings/ChangePassword";
import { PersonalSettings } from "@/pages/settings/PersonalSettings";
import { SecretsManager } from "@/pages/settings/SecretsManager";
import { SessionsPage } from "@/pages/settings/SessionsPage";
import { TenantSettings } from "@/pages/settings/TenantSettings";
import { useAuthStore } from "@/stores/auth";
import { type RouteName, useRouteStore } from "@/stores/route";

interface NavItem {
  route: RouteName;
  label: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { route: "settings.personal", label: "Cá nhân", icon: <User className="size-4" /> },
  {
    route: "settings.notifications",
    label: "Thông báo",
    icon: <Bell className="size-4" />,
  },
  {
    route: "settings.overrides",
    label: "Override field cá nhân",
    icon: <Layers className="size-4" />,
  },
  {
    route: "settings.presets",
    label: "UI Presets",
    icon: <TableIcon className="size-4" />,
  },
  { route: "settings.password", label: "Đổi mật khẩu", icon: <Lock className="size-4" /> },
  {
    route: "settings.sessions",
    label: "Phiên đăng nhập",
    icon: <Monitor className="size-4" />,
  },
  {
    route: "settings.apiTokens",
    label: "API Tokens",
    icon: <KeyRound className="size-4" />,
  },
  {
    route: "settings.account",
    label: "Tài khoản (Deactivate)",
    icon: <ShieldOff className="size-4" />,
  },
  {
    route: "settings.tenant",
    label: "Tenant (Admin)",
    icon: <SettingsIcon className="size-4" />,
    adminOnly: true,
  },
  {
    route: "settings.secrets",
    label: "API Keys (Admin)",
    icon: <Key className="size-4" />,
    adminOnly: true,
  },
];

export function SettingsLayout(): JSX.Element {
  const current = useRouteStore((s) => s.current);
  const navigate = useRouteStore((s) => s.navigate);
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles.includes("admin") ?? false;

  // Mặc định khi vào /settings → personal
  React.useEffect(() => {
    if (current === "settings") {
      navigate("settings.personal");
    }
  }, [current, navigate]);

  const visibleItems = NAV_ITEMS.filter((it) => !it.adminOnly || isAdmin);

  let body: React.ReactNode;
  switch (current) {
    case "settings.tenant":
      body = isAdmin ? <TenantSettings /> : <NoAccess />;
      break;
    case "settings.secrets":
      body = isAdmin ? <SecretsManager /> : <NoAccess />;
      break;
    case "settings.password":
      body = <ChangePassword />;
      break;
    case "settings.overrides":
      body = <PersonalOverrides />;
      break;
    case "settings.presets":
      body = <TablePresets />;
      break;
    case "settings.notifications":
      body = <NotificationPreferences />;
      break;
    case "settings.sessions":
      body = <SessionsPage />;
      break;
    case "settings.account":
      body = <AccountDeactivatePage />;
      break;
    case "settings.apiTokens":
      body = <ApiTokensPage />;
      break;
    case "settings.personal":
    case "settings":
    default:
      body = <PersonalSettings />;
  }

  return (
    <div className="flex-1 flex min-h-0">
      <aside className="w-56 shrink-0 border-r p-3 space-y-1">
        {visibleItems.map((it) => {
          const active = current === it.route;
          return (
            <button
              key={it.route}
              onClick={() => navigate(it.route)}
              className={
                "w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-left transition-colors " +
                (active
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted text-foreground")
              }
            >
              <Server className="size-4 hidden" />
              {it.icon}
              <span>{it.label}</span>
            </button>
          );
        })}
      </aside>
      <main className="flex-1 p-6 overflow-y-auto">{body}</main>
    </div>
  );
}

function NoAccess(): JSX.Element {
  return (
    <div className="text-center py-16 text-muted-foreground">
      Bạn không có quyền truy cập trang này (cần role admin).
    </div>
  );
}
